<template>
  <div :class="['w-full', labelClass]">
    <div v-if="!noLabel && displayLabel" class="flex items-baseline gap-1">
      <label :class="labelClasses">{{ displayLabel }}</label>
      <span v-if="hintLabel" class="text-sm text-gray-500 italic">{{
        hintLabel
      }}</span>
    </div>

    <div class="flex">
      <button
        v-if="buttons"
        type="button"
        class="flex items-center justify-center px-3 border border-gray-300 bg-gray-50 text-gray-600 rounded-l-lg border-r-0 transition-colors hover:bg-blue-500 hover:text-white hover:border-blue-500"
        :class="{ 'opacity-50 cursor-not-allowed': disabled }"
        :disabled="disabled"
        data-cy="previous-day-btn"
        @click="prevDay"
      >
        <svg
          class="w-3 h-3"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15.75 19.5L8.25 12l7.5-7.5"
          />
        </svg>
      </button>

      <!-- eslint-disable vue/no-v-model-argument -->
      <date-picker
        :key="pickerMode"
        v-model:value="date"
        v-bind="$attrs"
        :format="format"
        :value-type="valueType"
        :placeholder="placeholderLabel"
        :class="['seranova-datepicker w-full', pickerStateClass]"
        :input-class="inputClassName"
        :popup-class="popupClasses"
        :editable="isSheet ? false : editable"
        :disabled="disabled"
        :range="range"
        :input-attr="inputAttr"
        :open="isSheet ? false : undefined"
        @change="checkClearingData"
        @blur="emit('blur')"
        @pick="(evt: Date) => emit('pick', evt)"
        @update:open="onRequestOpen"
      >
        <template
          v-if="range && shortcuts && periods?.length"
          #footer="{ emit: footerEmit }"
        >
          <slot name="footer" :emit="footerEmit" />
        </template>
        <template #header>
          <slot name="header" />
        </template>
      </date-picker>
      <!-- eslint-enable vue/no-v-model-argument -->

      <DatepickerSheet
        v-if="sheetOpen"
        :model-value="modelValue"
        :range="range"
        :periods="sheetPeriods"
        :title="displayLabel || placeholderLabel"
        :format="format"
        @update:model-value="onSheetApply"
        @close="sheetOpen = false"
      />

      <button
        v-if="buttons"
        type="button"
        class="flex items-center justify-center px-3 border border-gray-300 bg-gray-50 text-gray-600 rounded-r-lg border-l-0 transition-colors hover:bg-blue-500 hover:text-white hover:border-blue-500"
        :class="{ 'opacity-50 cursor-not-allowed': disabled }"
        :disabled="disabled"
        data-cy="next-day-btn"
        @click="nextDay"
      >
        <svg
          class="w-3 h-3"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
          />
        </svg>
      </button>
    </div>

    <p v-if="errorMessage" class="mt-2 text-sm text-red-600">
      {{ errorMessage }}
    </p>
    <p
      v-else-if="helperText"
      class="mt-2 text-sm"
      :class="
        validation === 'success'
          ? 'text-green-600'
          : validation === 'error'
          ? 'text-red-600'
          : 'text-gray-500'
      "
    >
      {{ helperText }}
    </p>
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed } from "vue";
import DatePicker from "vue-datepicker-next";
import { findByName } from "@/lib/vuelidate_helpers";

import {
  DatepickerSheetAsync as DatepickerSheet,
  useDatepickerSheet
} from "@/components/Seranova/Datepicker/useDatepickerSheet";
import CONSTANTS from "@/Constants.js";
import type { ErrorObject } from "@vuelidate/core";
import "vue-datepicker-next/index.css";

const props = withDefaults(
  defineProps<{
    modelValue?: string | Date | Array<string | Date> | null;
    format?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    hintLabel?: string;
    noLabel?: boolean;
    disabled?: boolean;
    editable?: boolean;
    range?: boolean;
    buttons?: boolean;
    shortcuts?: boolean;
    periods?: Array<unknown> | null;
    valueType?: string;
    inputAttr?: Record<string, unknown>;
    labelClass?: string;
    popupClass?: string;
    // Legacy validation
    validationErrors?: ErrorObject[] | null;
    fieldError?: boolean;
    fieldErrorMessage?: string;
    // Seranova validation
    validation?: "error" | "success" | "none";
    helperText?: string;
  }>(),
  {
    modelValue: null,
    format: CONSTANTS.DATE_FORMAT,
    name: "",
    label: "",
    placeholder: "",
    hintLabel: "",
    noLabel: false,
    disabled: false,
    editable: true,
    range: false,
    buttons: false,
    shortcuts: false,
    periods: null,
    valueType: "format",
    inputAttr: () => ({}),
    labelClass: "",
    popupClass: "",
    validationErrors: null,
    fieldError: false,
    fieldErrorMessage: "",
    validation: "none",
    helperText: ""
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: string | Date | Array<string | Date> | null];
  pick: [value: Date];
  "clear-date": [];
  blur: [];
}>();

const displayLabel = computed(
  () => props.label || props.name || props.placeholder
);

const placeholderLabel = computed(() => props.placeholder || props.name);

const hasError = computed(() => {
  if (props.validation === "error") return true;
  if (props.fieldError || props.fieldErrorMessage) return true;
  if (props.validationErrors && props.name) {
    return !!findByName(props.validationErrors, props.name);
  }
  return false;
});

const hasSuccess = computed(() => props.validation === "success");

const errorMessage = computed(() => {
  if (props.fieldErrorMessage) return props.fieldErrorMessage;
  if (props.validationErrors && props.name) {
    return findByName(props.validationErrors, props.name) || "";
  }
  return "";
});

const labelClasses = computed(() => {
  const base = "block mb-2 text-sm font-medium";
  if (hasError.value) return `${base} text-red-700`;
  if (hasSuccess.value) return `${base} text-green-700`;
  if (props.disabled) return `${base} text-gray-400`;
  return `${base} text-gray-900`;
});

const pickerStateClass = computed(() => {
  if (hasError.value) return "seranova-datepicker--error";
  if (hasSuccess.value) return "seranova-datepicker--success";
  if (props.disabled) return "seranova-datepicker--disabled";
  return "";
});

const inputClassName = computed(() => {
  const classes = ["seranova-datepicker__input"];
  if (props.buttons) classes.push("seranova-datepicker__input--squared");
  return classes.join(" ");
});

const popupClasses = computed(() =>
  [
    "seranova-calendar-popup",
    props.popupClass,
    props.range ? "date-ranger-picker" : ""
  ]
    .filter(Boolean)
    .join(" ")
);

const date = computed({
  get() {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.map((d) => formatDate(d));
    }
    return formatDate(props.modelValue);
  },
  set(val: string | string[] | null) {
    if (Array.isArray(val)) {
      const parsed = val.map((d) => parseDate(d)).filter(Boolean) as string[];
      emit("update:modelValue", parsed.length ? parsed : null);
    } else {
      emit("update:modelValue", parseDate(val));
    }
  }
});

function formatDate(val: string | Date | null | undefined): string | null {
  if (!val) return null;
  if (moment(val, moment.ISO_8601).isValid()) {
    return moment(val).format(props.format);
  }
  return moment(val, "MM/DD/YYYY").format(props.format);
}

function parseDate(val: string | null): string | null {
  return val ? moment(val, props.format).toISOString() : val;
}

function checkClearingData(d: unknown) {
  if (!d || (Array.isArray(d) && !d[0] && !d[1])) {
    emit("clear-date");
  }
}

const {
  isSheet,
  sheetOpen,
  pickerMode,
  sheetPeriods,
  onRequestOpen,
  onSheetApply
} = useDatepickerSheet(props, {
  setDate: (value) => {
    date.value = value;
  },
  clearDate: () => emit("clear-date")
});

function prevDay() {
  if (typeof date.value === "string") {
    date.value = moment(date.value, props.format)
      .subtract(1, "days")
      .format(props.format);
  }
}

function nextDay() {
  if (typeof date.value === "string") {
    date.value = moment(date.value, props.format)
      .add(1, "days")
      .format(props.format);
  }
}
</script>

<style lang="scss">
/* Seranova-styled overrides for vue-datepicker-next */
.seranova-datepicker {
  /* Reset the library wrapper — inline-block + fixed width breaks flex layout */
  &.mx-datepicker {
    display: block !important;
    width: 100% !important;
  }

  /* Input field — matches Seranova InputField default state */
  .seranova-datepicker__input {
    height: 2.5rem; /* h-10, matches Seranova NumberInput */
    padding: 0 0.625rem; /* left p-2.5, right space for calendar icon */
    font-size: 0.875rem; /* text-sm */
    line-height: 1.25rem;
    color: #111827; /* text-gray-900 */
    background-color: #f9fafb; /* bg-gray-50 */
    border: 1px solid #d1d5db; /* border-gray-300 */
    border-radius: 0.5rem; /* rounded-lg */
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    box-shadow: none;
    width: 100%;

    &:focus {
      border-color: #3b82f6; /* border-blue-500 */
      box-shadow: 0 0 0 1px #3b82f6; /* ring-blue-500 */
    }

    &::placeholder {
      color: #9ca3af; /* placeholder-gray-400 */
    }

    /* Remove border-radius when between prev/next buttons */
    &--squared {
      border-radius: 0;
      border-left: none;
      border-right: none;
    }
  }

  /* Calendar icon */
  .mx-icon-calendar,
  .mx-icon-clear {
    color: #6b7280; /* text-gray-500 */
    right: 1.3rem;
  }

  /* Error state */
  &--error .seranova-datepicker__input {
    background-color: #fef2f2; /* bg-red-50 */
    border-color: #ef4444; /* border-red-500 */
    color: #7f1d1d; /* text-red-900 */

    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 1px #ef4444;
    }

    &::placeholder {
      color: #b91c1c; /* placeholder-red-700 */
    }
  }

  /* Success state */
  &--success .seranova-datepicker__input {
    background-color: #f0fdf4; /* bg-green-50 */
    border-color: #22c55e; /* border-green-500 */
    color: #14532d; /* text-green-900 */

    &:focus {
      border-color: #22c55e;
      box-shadow: 0 0 0 1px #22c55e;
    }

    &::placeholder {
      color: #15803d; /* placeholder-green-700 */
    }
  }

  /* Disabled state */
  &--disabled .seranova-datepicker__input {
    background-color: #e5e7eb; /* bg-gray-200 */
    color: #9ca3af; /* text-gray-400 */
    border-color: #d1d5db;
    cursor: not-allowed;

    &::placeholder {
      color: #6b7280;
    }
  }
}

/* Scoped popup styles — only targets CalendarDatepicker instances */
.seranova-calendar-popup.mx-datepicker-main.mx-datepicker-popup {
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  border: 1px solid #e5e7eb;

  .mx-calendar-content .cell.active {
    background-color: #3b82f6;
    border-radius: 0.375rem;
  }

  .mx-calendar-content .cell:hover {
    background-color: #dbeafe;
    border-radius: 0.375rem;
  }

  .mx-calendar-content .cell.active:hover {
    background-color: #2563eb;
  }

  .mx-calendar-content .cell.today {
    color: #3b82f6;
    font-weight: 600;
  }
}

/* Range picker sidebar — also scoped via the popup class */
.seranova-calendar-popup.date-ranger-picker.mx-datepicker-main {
  .mx-datepicker-content {
    display: flex;

    .mx-calendar-range {
      order: 2;
    }

    .mx-datepicker-footer {
      order: 1;
      border-right: 1px solid #e5e7eb;
    }
  }
}
</style>
